import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ServerDiscovery from '../services/ServerDiscovery';
import { useNavigate } from 'react-router-dom';
import { getDisplayName, getPlayerId, setDisplayName } from '../utils/playerId';
import { LANGLE_EVENTS, trackEvent, trackLangle } from '../utils/analytics';
import AuthPanel from './AuthPanel';
import AccountButton from './AccountButton';
import {
  authHeaders,
  getToken,
  getUser,
  setSession,
  signOut,
  subscribeAuth,
} from '../utils/auth';
import { captureShareCardImage, deliverShareImage } from '../utils/shareCardImage';
import { playCorrectSound } from '../utils/rewardSound';
import { AXES, GuessRow, LADDER, NO_MATCH, StepMeter, TAG_STATES, matchedAxes } from '../langle/ladder';
import PolyvoxPromo from './PolyvoxPromo';
import PolyvoxPopup from './PolyvoxPopup';

/* =========================================================================
 * Langle — daily audio-language puzzle.
 *
 * Structure:
 *   - Top bar:  back link · title · stats button · help button
 *   - Hero:     date + tagline + audio player
 *   - Board:    6 rows. Tint is the closest rung on the ladder; four pips
 *               score region / broader family / family / exact independently;
 *               tags mark those axes with ✓ / ≈. Shared with Account via
 *               src/langle/ladder.js.
 *   - Input:    autocomplete dropdown (only languages with audio available)
 *   - Reveal:   on game end, big card with native name, hello, fun fact
 *   - Stats:    modal with games-played / win % / streaks / histogram
 *
 * Persistence:
 *   localStorage["langle.state.<puzzleId>"]   - per-device in-progress + done state
 *   localStorage["langle.stats"]              - aggregated stats
 *   Signed-in players: completed games rehydrate from the server on new devices.
 *
 * Backend contract:
 *   GET  /daily                       → { puzzleId, audioUrl, maxGuesses, languages }
 *   GET  /daily/audio/<puzzleId>      → audio bytes (mp3)
 *   POST /daily/guess                 → { correct, sameFamily, sameMacroFamily, sameRegion, guess, family, macroFamily, region }
 *   GET  /daily/reveal/<puzzleId>     → { name, nativeName, family, macroFamily, region, speakers, writing, hello, helloRoman, funFact }
 * ========================================================================= */

const STATS_KEY = 'langle.stats';
// Classic (difficulty undefined/null) keeps the exact original key strings
// so existing in-progress games and stats are unaffected.
const stateKey = (id, difficulty) => `langle.state.${id}${difficulty ? `.${difficulty}` : ''}`;
const statsKey = (difficulty) => (difficulty ? `${STATS_KEY}.${difficulty}` : STATS_KEY);
const difficultyQS = (difficulty) => (difficulty ? `?difficulty=${difficulty}` : '');

function shareRowForGuess(g) {
  return matchedAxes(g)
    .map(({ short, hit }) => (hit ? short : '·'))
    .join(' ');
}

function formatPuzzleDate(iso) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// The bare /langle URL defaults to Easy from 2026-09-10 (see App.js), so a
// shared link has to name the actual track played or it'll send people to
// the wrong puzzle.
function sharePath(difficulty) {
  if (difficulty === 'easy') return 'langle/easy';
  if (difficulty === 'hard') return 'langle/hard';
  return 'langle/classic';
}

function buildShareString({ meta, done, guesses, playerName, difficulty }) {
  const used = guesses.length;
  const total = meta.maxGuesses;
  const score = done.won ? `${used}/${total}` : `X/${total}`;
  const date = formatPuzzleDate(meta.puzzleId);
  const name = (playerName || '').trim();
  const guessWord = used === 1 ? 'guess' : 'guesses';

  const summary = done.won
    ? (name ? `${name} solved in ${used} ${guessWord}.` : `Solved in ${used} ${guessWord}.`)
    : (name ? `${name} didn't solve today's puzzle.` : "Didn't solve today's puzzle.");

  const grid = guesses.map(shareRowForGuess).join('\n');

  return [
    'Langle · LangShazam',
    `${date} · ${score}`,
    '',
    summary,
    '',
    grid,
    'R · M · F · ✓',
    '',
    `langshazam.com/${sharePath(difficulty)}`,
  ].join('\n');
}

const SharePreview = forwardRef(function SharePreview({ meta, done, guesses, playerName, difficulty }, ref) {
  if (!meta || !done) return null;

  const used = guesses.length;
  const total = meta.maxGuesses;
  const score = done.won ? `${used}/${total}` : `X/${total}`;
  const name = (playerName || '').trim();
  const guessWord = used === 1 ? 'guess' : 'guesses';
  const summary = done.won
    ? (name ? `${name} solved in ${used} ${guessWord}.` : `Solved in ${used} ${guessWord}.`)
    : (name ? `${name} didn't solve today's puzzle.` : "Didn't solve today's puzzle.");

  return (
    <div ref={ref} className="lng-share-card" aria-label="Share preview">
      <div className="lng-share-card-top">
        <span className="lng-share-card-brand">LangShazam</span>
        <span className="lng-share-card-title">Langle</span>
      </div>
      <p className="lng-share-card-meta">
        {formatPuzzleDate(meta.puzzleId)} · {score}
      </p>
      <p className="lng-share-card-summary">{summary}</p>
      <div className="lng-share-card-grid">
        {guesses.map((g, rowIdx) => (
          <div key={rowIdx} className="lng-share-card-row">
            {matchedAxes(g).map(({ kind, hit }) => (
              <span
                key={kind}
                className={`lng-share-tile${hit ? ` is-${kind}` : ''}`}
              />
            ))}
          </div>
        ))}
      </div>
      <p className="lng-share-card-legend">Region · Macro · Family · Language</p>
      <p className="lng-share-card-url">langshazam.com/{sharePath(difficulty)}</p>
    </div>
  );
});

function shareButtonLabel(feedback, sharing) {
  if (sharing) return 'Preparing…';
  if (feedback === 'shared') return '✓ Shared';
  if (feedback === 'text') return '✓ Copied';
  if (feedback === 'copied') return '✓ Image copied';
  if (feedback === 'downloaded') return '✓ Image saved';
  return 'Share results';
}

async function copyShareText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function loadState(puzzleId, difficulty) {
  try {
    const raw = localStorage.getItem(stateKey(puzzleId, difficulty));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.puzzleId === puzzleId) return parsed;
    return null;
  } catch { return null; }
}
function saveState(state, difficulty) {
  try { localStorage.setItem(stateKey(state.puzzleId, difficulty), JSON.stringify(state)); } catch {}
}

async function fetchAccountPlay(base, puzzleId, difficulty) {
  if (!getToken()) return null;
  try {
    const res = await fetch(`${base}/daily/archive/${puzzleId}${difficultyQS(difficulty)}`, { headers: authHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.play ? data : null;
  } catch {
    return null;
  }
}

function stateFromArchive(puzzleId, archive) {
  if (!archive?.play) return null;
  const guesses = archive.play.guesses || [];
  if (!archive.reveal) return { puzzleId, guesses, done: null };
  return {
    puzzleId,
    guesses,
    done: { won: archive.play.won, answer: archive.reveal },
  };
}

async function resolvePuzzleState(base, puzzleId, difficulty) {
  const cached = loadState(puzzleId, difficulty);
  if (cached?.done) return cached;

  const archive = await fetchAccountPlay(base, puzzleId, difficulty);
  const remote = archive ? stateFromArchive(puzzleId, archive) : null;
  if (remote?.done) {
    saveState(remote, difficulty);
    return remote;
  }
  return cached;
}

async function applyPuzzleState(base, puzzleId, state, { setGuesses, setDone, setStats, reportCompletion }, difficulty) {
  if (!state) return;
  setGuesses(state.guesses || []);
  if (!state.done) return;

  let done = state.done;
  const a = done.answer;
  const isStale = !a || !a.nativeName || !a.funFact;
  if (isStale) {
    try {
      const fresh = await fetch(`${base}/daily/reveal/${puzzleId}${difficultyQS(difficulty)}`).then((r) => r.json());
      done = { ...state.done, answer: fresh };
      saveState({ puzzleId, guesses: state.guesses || [], done }, difficulty);
    } catch { /* keep cached answer */ }
  }
  setDone(done);
  setStats((prev) => recordGameOutcome(prev, {
    puzzleId,
    won: done.won,
    guessCount: (state.guesses || []).length,
    difficulty,
  }));
  reportCompletion(
    base,
    puzzleId,
    done.won,
    (state.guesses || []).length,
    state.guesses || [],
  );
}

const EMPTY_STATS = {
  played: 0,
  wins: 0,
  currentStreak: 0,
  maxStreak: 0,
  lastWonPuzzleId: null,
  lastFinishedPuzzleId: null,
  // distribution[i] = number of wins in i+1 guesses (i goes 0..5)
  distribution: [0, 0, 0, 0, 0, 0],
};

function loadStats(difficulty) {
  try {
    const parsed = JSON.parse(localStorage.getItem(statsKey(difficulty)));
    if (!parsed) return { ...EMPTY_STATS };
    return { ...EMPTY_STATS, ...parsed,
      distribution: parsed.distribution?.length === 6
        ? parsed.distribution
        : EMPTY_STATS.distribution };
  } catch { return { ...EMPTY_STATS }; }
}
function saveStats(s, difficulty) {
  try { localStorage.setItem(statsKey(difficulty), JSON.stringify(s)); } catch {}
}

function previousIsoDate(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

function recordGameOutcome(prev, { puzzleId, won, guessCount, difficulty }) {
  // Idempotent — repeated calls for the same puzzleId are no-ops.
  if (prev.lastFinishedPuzzleId === puzzleId) return prev;
  const next = {
    ...prev,
    played: prev.played + 1,
    wins: prev.wins + (won ? 1 : 0),
    distribution: [...prev.distribution],
    lastFinishedPuzzleId: puzzleId,
  };
  if (won) {
    next.distribution[guessCount - 1] = (next.distribution[guessCount - 1] || 0) + 1;
    const continuing = prev.lastWonPuzzleId === previousIsoDate(puzzleId);
    next.currentStreak = continuing ? prev.currentStreak + 1 : 1;
    next.maxStreak = Math.max(prev.maxStreak, next.currentStreak);
    next.lastWonPuzzleId = puzzleId;
  } else {
    next.currentStreak = 0;
  }
  saveStats(next, difficulty);
  return next;
}

// ---- countdown ----

function msUntilNextUtcMidnight() {
  const now = new Date();
  const next = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0,
  ));
  return Math.max(0, next.getTime() - now.getTime());
}
function formatHms(ms) {
  const total = Math.floor(ms / 1000);
  const h = String(Math.floor(total / 3600)).padStart(2, '0');
  const m = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

// ---- icons ----

const PlayIcon  = () => (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>);
const PauseIcon = () => (<svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>);
const StatsIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6"  y1="20" x2="6"  y2="14" /></svg>);
const HelpIcon  = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>);
const CloseIcon = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
// Two figures with a "+" suggests "create a group" without needing copy.
// Drawn matching the stroke weight of HelpIcon / StatsIcon so the topbar
// reads as one coherent icon row.
const GroupIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M20 11v6" />
    <path d="M17 14h6" />
  </svg>
);

// Classic is retired from the game UI (2026-09-11) — Easy/Hard are the only
// two picks here now. The /langle/classic route, its data, and its history
// on the account page all still work; it's just no longer offered as a tab
// so new/returning players land on Easy or Hard instead.
const DIFFICULTY_TABS = [
  { value: 'easy', label: 'Easy', path: '/langle/easy' },
  { value: 'hard', label: 'Hard', path: '/langle/hard' },
];

function DifficultyTabs({ active }) {
  return (
    <nav className="lng-difficulty-tabs" aria-label="Difficulty">
      {DIFFICULTY_TABS.map((tab) => (
        <Link
          key={tab.label}
          to={tab.path}
          className={`lng-difficulty-tab${tab.value === active ? ' is-active' : ''}`}
        >
          {tab.label}
        </Link>
      ))}
    </nav>
  );
}


// =========================================================================
// Stats modal
// =========================================================================

function StatsModal({ stats, puzzleId, lastGuessRow, onClose, onShare, shareFeedback, shareSharing, canShare, playerName, onPlayerNameChange, user, onSignOut, sharePreview }) {
  const [countdown, setCountdown] = useState(formatHms(msUntilNextUtcMidnight()));
  useEffect(() => {
    const id = setInterval(() => setCountdown(formatHms(msUntilNextUtcMidnight())), 1000);
    return () => clearInterval(id);
  }, []);

  const winRate = stats.played > 0 ? Math.round((stats.wins / stats.played) * 100) : 0;
  const maxBar = Math.max(1, ...stats.distribution);

  return (
    <div className="lng-modal-overlay" onClick={onClose}>
      <div className="lng-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lng-modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        <h2 className="lng-modal-title">Statistics</h2>

        <div className="lng-stats-row">
          <div><strong>{stats.played}</strong><span>Played</span></div>
          <div><strong>{winRate}</strong><span>Win %</span></div>
          <div><strong>{stats.currentStreak}</strong><span>Current<br />streak</span></div>
          <div><strong>{stats.maxStreak}</strong><span>Max<br />streak</span></div>
        </div>

        <h3 className="lng-modal-subtitle">Guess distribution</h3>
        <div className="lng-histogram">
          {stats.distribution.map((count, i) => {
            const isCurrent = lastGuessRow === i + 1 && stats.lastFinishedPuzzleId === puzzleId;
            const width = `${Math.max(8, (count / maxBar) * 100)}%`;
            return (
              <div className="lng-histogram-row" key={i}>
                <span className="lng-histogram-label">{i + 1}</span>
                <div className="lng-histogram-bar-wrap">
                  <div
                    className={`lng-histogram-bar${isCurrent ? ' is-current' : ''}${count === 0 ? ' is-empty' : ''}`}
                    style={{ width }}
                  >
                    <span>{count}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {canShare && (
          <div className="lng-name-row">
            <label className="lng-name-label" htmlFor="langle-name">
              Your name <span className="lng-name-hint">(shown when you share)</span>
            </label>
            <input
              id="langle-name"
              className="lng-name-input"
              type="text"
              value={playerName || ''}
              onChange={(e) => onPlayerNameChange?.(e.target.value)}
              placeholder="e.g. Kaan"
              maxLength={24}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}

        {user ? (
          <p className="lng-signed-in">
            Signed in as {user.name || user.email}
            {' · '}
            <Link to="/account">Account</Link>
            {' · '}
            <button type="button" className="lng-text-btn" onClick={onSignOut}>Sign out</button>
          </p>
        ) : (
          <div className="lng-save-streak lng-save-streak-compact">
            <p>Save your streak across phones.</p>
            <AuthPanel nextPath={null} onSuccess={() => {}} />
          </div>
        )}

        <div className="lng-stats-footer">
          <div className="lng-countdown">
            <div className="lng-countdown-label">Next Langle</div>
            <div className="lng-countdown-time">{countdown}</div>
          </div>
          <button className="lng-share-btn" onClick={onShare} disabled={!canShare || shareSharing}>
            {shareButtonLabel(shareFeedback, shareSharing)}
          </button>
        </div>

        {canShare && sharePreview}
      </div>
    </div>
  );
}


// =========================================================================
// Group create modal — single-purpose dialog for spinning up a private
// group. Pulled out of the stats modal so the entry point can live on the
// topbar and the post-game CTA row instead of being buried two screens
// deep inside "View stats". The dialog only collects what the server needs
// (display name) and immediately navigates to /group/<id> on success.
// =========================================================================

function GroupCreateModal({ onClose, name, onNameChange, onCreate, creating, error, accountName }) {
  // Submit on Enter so the dialog feels like a one-step form rather than a
  // "type, hunt for the button" flow.
  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (name || '').trim() && !creating) {
      e.preventDefault();
      onCreate();
    }
  };
  return (
    <div className="lng-modal-overlay" onClick={onClose}>
      <div className="lng-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lng-modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        <h2 className="lng-modal-title">Play with friends</h2>
        <p className="lng-help-intro" style={{ fontSize: '0.9rem', textAlign: 'center', marginTop: '0.25rem' }}>
          Create a private leaderboard for today's puzzle. Share the link, then watch your friends try to beat your score.
        </p>
        {accountName ? (
          <p className="lng-help-intro" style={{ fontSize: '0.9rem', textAlign: 'center' }}>
            Creating as <strong>{accountName}</strong>
          </p>
        ) : (
          <div className="lng-name-row">
            <label className="lng-name-label" htmlFor="lng-group-create-name">
              Your name <span className="lng-name-hint">(shown on the leaderboard)</span>
            </label>
            <input
              id="lng-group-create-name"
              className="lng-name-input"
              type="text"
              value={name || ''}
              onChange={(e) => onNameChange?.(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="e.g. Kaan"
              maxLength={24}
              autoComplete="off"
              spellCheck={false}
              autoFocus
            />
          </div>
        )}
        <button
          className="lng-share-btn lng-share-primary"
          style={{ width: '100%' }}
          onClick={onCreate}
          disabled={creating || !(accountName || name || '').trim()}
        >
          {creating ? 'Creating…' : 'Create group'}
        </button>
        {error && <p className="lng-inline-error" style={{ marginTop: '0.5rem' }}>{error}</p>}
      </div>
    </div>
  );
}


// =========================================================================
// Help modal
// =========================================================================

function HelpModal({ onClose }) {
  return (
    <div className="lng-modal-overlay" onClick={onClose}>
      <div className="lng-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lng-modal-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        <h2 className="lng-modal-title">How to play</h2>
        <p className="lng-help-intro">
          Listen and guess the language. You have <strong>6 tries</strong>.
        </p>
        <p className="lng-help-intro">
          After each guess, the row tells you how close you were.{' '}
          <strong>The darker the row, the closer you are</strong>:
        </p>
        <ul className="lng-help-list">
          {[...LADDER, NO_MATCH].map(({ className, label }) => (
            <li key={className}>
              <span className={`lng-tile-swatch ${className}`} />
              {label}
            </li>
          ))}
        </ul>
        <p className="lng-help-intro">
          The four bars beside a guess are scored one at a time, so they show exactly which
          hints landed. A language can share a family without sharing a region:
        </p>
        <ul className="lng-help-list">
          {AXES.map(({ key, kind, name }) => (
            <li key={kind}>
              <StepMeter guess={{ [key]: true }} />
              {name}
            </li>
          ))}
        </ul>
        <p className="lng-help-intro">
          Each row also lists that guess's region and language family. A{' '}
          <strong>{TAG_STATES.on.mark}</strong> marks a part that matched today's answer, and{' '}
          <strong>{TAG_STATES.half.mark}</strong> means only the broader family did.
        </p>
        <p className="lng-help-intro">
          A new puzzle drops every day at <strong>00:00 UTC</strong>. Same audio for everyone — compare your score with friends.
        </p>
        <h3 className="lng-modal-subtitle">Reading a shared grid</h3>
        <p className="lng-help-intro">
          Each row is one guess, four squares wide:
        </p>
        <div className="lng-share-legend">
          <span className="lng-share-legend-row">R · M · F · ✓</span>
          <span className="lng-share-legend-cap">region · macro · family · language</span>
        </div>
        <p className="lng-help-intro" style={{ fontSize: '0.85rem' }}>
          A square lights up when the guess matches that axis. ⬛ means a miss. A row of all four colors means a winning guess.
        </p>
        <p className="lng-help-attribution">
          Audio courtesy of <a href="https://huggingface.co/datasets/google/fleurs" target="_blank" rel="noopener noreferrer">Google FLEURS</a> and <a href="https://huggingface.co/datasets/TalTechNLP/voxlingua107_wds" target="_blank" rel="noopener noreferrer">VoxLingua107</a> · CC-BY-4.0
        </p>
      </div>
    </div>
  );
}


// =========================================================================
// Reveal card (drops in after game ends)
// =========================================================================

function RevealCell({ label, value }) {
  if (!value) return null;
  return (
    <div className="lng-reveal-cell">
      <div className="lng-reveal-key">{label}</div>
      <div className="lng-reveal-val">{value}</div>
    </div>
  );
}

function RevealCard({ won, guessesUsed, maxGuesses, answer, globalStats }) {
  const score = won ? `${guessesUsed}/${maxGuesses}` : `X/${maxGuesses}`;
  const familyLabel = [answer.family, answer.macroFamily]
    .filter(Boolean)
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .join(' · ');
  return (
    <div className="lng-reveal">
      <div className="lng-reveal-header">
        <span className={`lng-reveal-result ${won ? 'won' : 'lost'}`}>
          {won ? `Solved · ${score}` : `Out of guesses · ${score}`}
        </span>
      </div>

      <div className="lng-reveal-name">
        <div className="lng-reveal-english">{answer.name}</div>
        {answer.nativeName && answer.nativeName !== answer.name && (
          <div className="lng-reveal-native" lang="auto">{answer.nativeName}</div>
        )}
      </div>

      <div className="lng-reveal-grid">
        <RevealCell label="Region"   value={answer.region} />
        <RevealCell label="Family"   value={familyLabel} />
        <RevealCell label="Speakers" value={answer.speakers} />
        <RevealCell label="Writing"  value={answer.writing} />
      </div>

      {answer.hello && (
        <div className="lng-reveal-hello">
          <span className="lng-reveal-hello-label">Say hello</span>
          <span className="lng-reveal-hello-native" lang="auto">{answer.hello}</span>
          {answer.helloRoman && <span className="lng-reveal-hello-roman">{answer.helloRoman}</span>}
        </div>
      )}

      {answer.funFact && (
        <div className="lng-reveal-fact">
          <span className="lng-reveal-fact-label">Did you know</span>
          <p>{answer.funFact}</p>
        </div>
      )}

      {globalStats?.showStats && (
        <div className="lng-global-stats">
          <div className="lng-global-headline">
            {won && globalStats.fasterThan != null
              ? `Faster than ${globalStats.fasterThan}% of today's players`
              : `${globalStats.winRate}% of players solved it today`}
          </div>
          <div className="lng-global-row lng-global-row--two">
            <div>
              <strong>{globalStats.winRate}%</strong>
              <span>solved it</span>
            </div>
            <div>
              <strong>{globalStats.avgGuesses ?? '—'}</strong>
              <span>avg guesses</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =========================================================================
// Main component
// =========================================================================

// `difficulty`: undefined = classic (the original full-pool daily, unchanged
// behavior/storage keys). "easy"/"hard" render the same UI against a fully
// independent puzzle, deck rotation, and set of localStorage keys.
function Daily({ difficulty } = {}) {
  const [httpBase, setHttpBase] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');

  const [guesses, setGuesses] = useState([]);
  const [done, setDone] = useState(null);   // { won, answer } | null
  // Set only when a game finishes in this visit (not when a finished one is restored).
  const [finishedAt, setFinishedAt] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const audioRef = useRef(null);
  const listenTrackedRef = useRef(false);

  const [stats, setStats] = useState(() => loadStats(difficulty));
  const [showStats, setShowStats] = useState(false);
  const [showHelp,  setShowHelp]  = useState(false);
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [shareFeedback, setShareFeedback] = useState('');
  const [shareSharing, setShareSharing] = useState(false);
  const shareCardRef = useRef(null);
  const [shakeRow, setShakeRow] = useState(false);

  // Anonymous server-side stats for *today's* puzzle. Populated after the
  // game ends (or on cached-done load). null until we hear back.
  const [globalStats, setGlobalStats] = useState(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupError, setGroupError] = useState('');
  const playerId = useMemo(() => getPlayerId(), []);
  const [user, setUser] = useState(getUser);
  useEffect(() => subscribeAuth(setUser), []);
  useEffect(() => {
    // Server-side account_stats is classic-only for now (see langle_db.py) —
    // don't let this clobber the easy/hard in-memory `stats` with classic data.
    if (!httpBase || !getToken() || difficulty) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${httpBase}/auth/me`, { headers: authHeaders() });
        if (cancelled) return;
        if (res.status === 401) { signOut(); return; }
        if (!res.ok) return;
        const data = await res.json();
        setSession(getToken(), data.user);
        if (data.stats) {
          const next = { ...EMPTY_STATS, ...data.stats };
          saveStats(next, difficulty);
          setStats(next);
        }
      } catch { /* offline */ }
    })();
    return () => { cancelled = true; };
  }, [httpBase, difficulty]);
  // Display name flows into the share string and the group join payload.
  // Lazily seeded from localStorage so a returning player keeps their handle
  // across sessions; persisted via setDisplayName on every edit.
  const [playerName, setPlayerNameState] = useState(() => getDisplayName());
  const updatePlayerName = useCallback((next) => {
    const cleaned = (next || '').trim().slice(0, 24);
    setPlayerNameState(cleaned);
    setDisplayName(cleaned);
  }, []);
  useEffect(() => {
    if (user?.name && !getDisplayName()) updatePlayerName(user.name);
  }, [user, updatePlayerName]);
  const navigate = useNavigate();

  // Centralised /daily/complete call so the finish-now path and the
  // refresh-rehydrate path use the same logic. Best-effort — failures are
  // swallowed because the local game is already complete and usable.
  const reportCompletion = useCallback(async (base, puzzleId, won, guessCount, guesses) => {
    try {
      const res = await fetch(`${base}/daily/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ puzzleId, playerId, won, guessCount, guesses, difficulty }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setGlobalStats(data);
    } catch { /* offline / backend down — don't surface, local UI still works */ }
  }, [playerId, difficulty]);

  // Bootstrap.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = await ServerDiscovery.discoverHttpBase();
        if (cancelled) return;
        setHttpBase(base);
        const res = await fetch(`${base}/daily${difficultyQS(difficulty)}`);
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail.detail || `Daily not ready (HTTP ${res.status})`);
        }
        const data = await res.json();
        if (cancelled) return;
        setMeta(data);

        const state = await resolvePuzzleState(base, data.puzzleId, difficulty);
        trackLangle(LANGLE_EVENTS.VIEW, {
          puzzle_id: data.puzzleId,
          difficulty,
          resumed: Boolean(state),
          finished: Boolean(state?.done),
        });
        if (state) {
          await applyPuzzleState(base, data.puzzleId, state, {
            setGuesses,
            setDone,
            setStats,
            reportCompletion,
          }, difficulty);
        }
        // Surface help modal on the very first visit.
        if (!localStorage.getItem('langle.seen-help')) {
          setShowHelp(true);
          trackLangle(LANGLE_EVENTS.HELP, { source: 'auto' });
          localStorage.setItem('langle.seen-help', '1');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [difficulty]);

  // If the player signs in on a fresh device, pull today's finished game
  // from their account (localStorage is per-browser).
  useEffect(() => {
    if (!httpBase || !meta?.puzzleId || !user || done) return undefined;
    let cancelled = false;
    (async () => {
      if (loadState(meta.puzzleId, difficulty)?.done) return;
      const archive = await fetchAccountPlay(httpBase, meta.puzzleId, difficulty);
      if (cancelled || !archive) return;
      const state = stateFromArchive(meta.puzzleId, archive);
      if (!state?.done) return;
      saveState(state, difficulty);
      await applyPuzzleState(httpBase, meta.puzzleId, state, {
        setGuesses,
        setDone,
        setStats,
        reportCompletion,
      }, difficulty);
    })();
    return () => { cancelled = true; };
  }, [httpBase, meta?.puzzleId, user, done, reportCompletion, difficulty]);

  useEffect(() => {
    listenTrackedRef.current = false;
  }, [meta?.puzzleId]);

  const trackFirstListen = useCallback(() => {
    if (!meta?.puzzleId || listenTrackedRef.current) return;
    listenTrackedRef.current = true;
    trackLangle(LANGLE_EVENTS.LISTEN, { puzzle_id: meta.puzzleId });
  }, [meta?.puzzleId]);

  // Audio events.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay  = () => {
      setIsPlaying(true);
      trackFirstListen();
    };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setPlayCount((count) => {
        const next = count + 1;
        if (next > 1 && meta?.puzzleId) {
          trackLangle(LANGLE_EVENTS.REPLAY, {
            puzzle_id: meta.puzzleId,
            play_count: next,
          });
        }
        return next;
      });
    };
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
    };
  }, [meta?.audioUrl, meta?.puzzleId, trackFirstListen]);

  const togglePlay = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.currentTime = 0;
      a.play()
        .then(() => trackFirstListen())
        .catch(() => {});
    } else {
      a.pause();
    }
  }, [trackFirstListen]);

  // Autocomplete.
  const guessedNames = useMemo(
    () => new Set(guesses.map((g) => g.guess.toLowerCase())),
    [guesses],
  );

  const suggestions = useMemo(() => {
    if (!meta || !query.trim()) return [];
    const q = query.trim().toLowerCase();
    const matches = meta.languages.filter(
      (name) => name.toLowerCase().includes(q) && !guessedNames.has(name.toLowerCase()),
    );
    matches.sort((a, b) => {
      const aStarts = a.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.toLowerCase().startsWith(q) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      return a.localeCompare(b);
    });
    return matches.slice(0, 8);
  }, [meta, query, guessedNames]);

  const pickGuess = useCallback(() => {
    const q = query.trim();
    if (!q) return '';
    const lower = q.toLowerCase();
    const exact = (meta?.languages || []).find((name) => name.toLowerCase() === lower);
    if (exact) return exact;
    return suggestions[highlighted] || q;
  }, [query, meta, suggestions, highlighted]);

  useEffect(() => { setHighlighted(0); }, [query]);

  // Submit a guess.
  const submitGuess = useCallback(async (rawGuess) => {
    if (!meta || done || submitting) return;
    const guessName = (rawGuess || '').trim();
    if (!guessName) return;
    if (guessedNames.has(guessName.toLowerCase())) {
      setError('Already guessed.');
      setShakeRow(true); setTimeout(() => setShakeRow(false), 500);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${httpBase}/daily/guess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ puzzleId: meta.puzzleId, guess: guessName, difficulty }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Guess failed (HTTP ${res.status})`);
      }
      const result = await res.json();
      const nextGuesses = [...guesses, result];
      setGuesses(nextGuesses);
      setQuery('');

      const used = nextGuesses.length;
      const won = result.correct;
      const lost = !won && used >= meta.maxGuesses;

      if (won) playCorrectSound();

      trackLangle(LANGLE_EVENTS.GUESS, {
        puzzle_id: meta.puzzleId,
        guess_number: used,
        correct: won,
      });

      if (won || lost) {
        const reveal = await fetch(`${httpBase}/daily/reveal/${meta.puzzleId}${difficultyQS(difficulty)}`).then((r) => r.json());
        const finishedState = { won, answer: reveal };
        setDone(finishedState);
        setFinishedAt(Date.now());
        saveState({ puzzleId: meta.puzzleId, guesses: nextGuesses, done: finishedState }, difficulty);
        const newStats = recordGameOutcome(stats, {
          puzzleId: meta.puzzleId, won, guessCount: used, difficulty,
        });
        setStats(newStats);
        reportCompletion(httpBase, meta.puzzleId, won, used, nextGuesses);
        trackLangle(LANGLE_EVENTS.COMPLETE, {
          puzzle_id: meta.puzzleId,
          difficulty,
          outcome: won ? 'won' : 'lost',
          guess_count: used,
          listened: listenTrackedRef.current,
        });
        setTimeout(() => setShowStats(true), 1800);
      } else {
        saveState({ puzzleId: meta.puzzleId, guesses: nextGuesses, done: null }, difficulty);
      }
    } catch (e) {
      setError(e.message || String(e));
      setShakeRow(true); setTimeout(() => setShakeRow(false), 500);
    } finally {
      setSubmitting(false);
    }
  }, [meta, done, submitting, guesses, httpBase, guessedNames, stats, reportCompletion, difficulty]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      submitGuess(pickGuess());
    }
  };

  const shareString = useMemo(() => {
    if (!meta || !done) return '';
    return buildShareString({ meta, done, guesses, playerName, difficulty });
  }, [meta, done, guesses, playerName, difficulty]);

  const sharePreview = useMemo(() => {
    if (!meta || !done) return null;
    return (
      <SharePreview
        ref={shareCardRef}
        meta={meta}
        done={done}
        guesses={guesses}
        playerName={playerName}
        difficulty={difficulty}
      />
    );
  }, [meta, done, guesses, playerName, difficulty]);

  // Share the rendered card as a PNG (native sheet / clipboard / download).
  // Plain text is only used if image capture fails.
  const onShare = useCallback(async () => {
    if (!shareString) return;
    setShareSharing(true);
    setShareFeedback('');
    try {
      const blob = await captureShareCardImage(shareCardRef.current);
      if (blob) {
        const result = await deliverShareImage(blob);
        if (result === 'aborted') return;
        if (result) {
          setShareFeedback(result);
          setTimeout(() => setShareFeedback(''), 2200);
          trackLangle(LANGLE_EVENTS.SHARE, { kind: 'results_image' });
          return;
        }
      }
      const copied = await copyShareText(shareString);
      if (!copied) return;
      setShareFeedback('text');
      setTimeout(() => setShareFeedback(''), 2200);
      trackLangle(LANGLE_EVENTS.SHARE, { kind: 'results_text' });
    } catch {
      const copied = await copyShareText(shareString);
      if (!copied) return;
      setShareFeedback('text');
      setTimeout(() => setShareFeedback(''), 2200);
      trackLangle(LANGLE_EVENTS.SHARE, { kind: 'results_text' });
    } finally {
      setShareSharing(false);
    }
  }, [shareString]);

  const canShare = Boolean(shareString);

  // Open the group-create dialog from any entry point. Wipes any stale
  // error from a previous attempt so the dialog opens clean.
  const openGroupCreate = useCallback(() => {
    setGroupError('');
    setShowGroupCreate(true);
    trackLangle(LANGLE_EVENTS.GROUP_OPEN, { puzzle_id: meta?.puzzleId || 'unknown' });
  }, [meta?.puzzleId]);
  const closeGroupCreate = useCallback(() => {
    setShowGroupCreate(false);
    setGroupError('');
  }, []);

  // Create-and-redirect: spin up a private group on the server, then take
  // the user to its leaderboard so they can copy the invite link.
  const onCreateGroup = useCallback(async () => {
    if (!httpBase || creatingGroup) return;
    // Signed in: always the account name, no typed override — matches the
    // group leaderboard page, which auto-joins signed-in players the same way.
    const trimmedName = (user?.name || playerName || '').trim();
    if (!trimmedName) {
      // Don't silently store "You" / "Anonymous" — that's what bites people
      // when they share the invite link. Force the user to type a real name
      // here (the input lives just above this button in the modal).
      setGroupError('Enter your name above first — your friends will see it on the leaderboard.');
      return;
    }
    setCreatingGroup(true);
    setGroupError('');
    try {
      const res = await fetch(`${httpBase}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          playerId,
          displayName: trimmedName,
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const { groupId } = await res.json();
      trackLangle(LANGLE_EVENTS.GROUP_CREATE, { puzzle_id: meta?.puzzleId || 'unknown' });
      setShowStats(false);
      setShowGroupCreate(false);
      navigate(`/group/${groupId}`);
    } catch (e) {
      setGroupError(e.message || String(e));
    } finally {
      setCreatingGroup(false);
    }
  }, [httpBase, creatingGroup, playerId, playerName, navigate, user]);

  // ---------------- render ----------------

  if (error && !meta) {
    return (
      <div className="lng-page">
        <header className="lng-topbar">
          <Link to="/" className="lng-back">←</Link>
          <h1 className="lng-brand">Langle</h1>
          <div className="lng-topbar-actions">
            <AccountButton />
          </div>
        </header>
        <DifficultyTabs active={difficulty} />
        <div className="lng-error-card">
          <p>{error}</p>
          {!/starts \d{4}-\d{2}-\d{2}/.test(error) && (
            <p className="lng-error-hint">If you're the operator: run <code>python -m scripts.build_daily_dataset</code> in <code>backend/</code>.</p>
          )}
        </div>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="lng-page">
        <header className="lng-topbar">
          <Link to="/" className="lng-back">←</Link>
          <h1 className="lng-brand">Langle</h1>
          <div className="lng-topbar-actions">
            <AccountButton />
          </div>
        </header>
        <DifficultyTabs active={difficulty} />
        <p className="lng-loading">Loading today's puzzle…</p>
      </div>
    );
  }

  const audioSrc = `${httpBase}${meta.audioUrl}`;
  const activeRow = guesses.length;

  // Pretty date for the hero ("April 27, 2026").
  const prettyDate = new Date(`${meta.puzzleId}T00:00:00Z`)
    .toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

  return (
    <div className="lng-page">
      <header className="lng-topbar">
        <Link to="/" className="lng-back" aria-label="Back to LangShazam">←</Link>
        <h1 className="lng-brand">Langle</h1>
        <div className="lng-topbar-actions">
          <button className="lng-icon-btn" onClick={() => { setShowHelp(true); trackLangle(LANGLE_EVENTS.HELP, { source: 'button' }); }} aria-label="How to play"><HelpIcon /></button>
          <button className="lng-icon-btn" onClick={openGroupCreate}          aria-label="Play with friends"><GroupIcon /></button>
          <button className="lng-icon-btn" onClick={() => { setShowStats(true); trackLangle(LANGLE_EVENTS.STATS, { source: 'button' }); }} aria-label="Statistics"><StatsIcon /></button>
          <AccountButton />
        </div>
      </header>

      <DifficultyTabs active={difficulty} />

      <section className="lng-hero">
        <div className="lng-hero-date">Daily audio puzzle · {prettyDate}</div>
        <p className="lng-hero-tagline">
          Listen and guess the language. {meta.maxGuesses} tries.
          {difficulty === 'easy' && ' Drawn from widely-known languages.'}
          {difficulty === 'hard' && ' Drawn from the full obscure-language pool.'}
        </p>
      </section>

      <section className={`lng-audio-card${isPlaying ? ' is-playing' : ''}`}>
        <button
          className="lng-audio-btn"
          onClick={togglePlay}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <div className="lng-audio-bars" aria-hidden>
          {Array.from({ length: 22 }).map((_, i) => (
            <span key={i} style={{ animationDelay: `${(i % 7) * 0.07}s` }} />
          ))}
        </div>
        <div className="lng-audio-meta">
          <span className="lng-audio-label">Today's clip</span>
          <span className="lng-audio-sub">{playCount} {playCount === 1 ? 'play' : 'plays'} · unlimited replays</span>
        </div>
        <audio ref={audioRef} src={audioSrc} preload="auto" />
      </section>

      <section className="lng-board">
        {Array.from({ length: meta.maxGuesses }).map((_, i) => {
          const g = guesses[i];
          const isActive = i === activeRow && !done;
          if (!g) {
            return (
              <div
                key={i}
                className={`lng-row${isActive ? ' is-active' : ''}${isActive && shakeRow ? ' is-shaking' : ''}`}
              >
                <span className="lng-row-text">{isActive ? '— your turn —' : ''}</span>
              </div>
            );
          }
          return <GuessRow key={i} guess={g} />;
        })}
      </section>

      {!done && (
        <section className="lng-input-row">
          <div className="lng-autocomplete">
            <input
              ref={inputRef}
              className="lng-input"
              placeholder={`Guess #${guesses.length + 1} of ${meta.maxGuesses}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              disabled={submitting}
              autoComplete="off"
              autoFocus
            />
            {suggestions.length > 0 && (
              <ul className="lng-suggestions">
                {suggestions.map((name, idx) => (
                  <li
                    key={name}
                    className={idx === highlighted ? 'is-highlighted' : ''}
                    onMouseDown={(e) => { e.preventDefault(); submitGuess(name); }}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="lng-submit"
            onMouseDown={(e) => {
              // preventDefault keeps the input from blurring first, which
              // otherwise eats the click on mobile (keyboard dismiss / layout
              // shift) and when the suggestion list covers the button.
              e.preventDefault();
              submitGuess(pickGuess());
            }}
            disabled={submitting || !query.trim()}
          >
            Guess
          </button>
        </section>
      )}

      {error && meta && <p className="lng-inline-error">{error}</p>}

      {done && (
        <RevealCard
          won={done.won}
          guessesUsed={guesses.length}
          maxGuesses={meta.maxGuesses}
          answer={done.answer}
          globalStats={globalStats}
        />
      )}

      {done && (
        <>
          <div className="lng-cta-row">
            <button className="lng-share-btn lng-share-primary" onClick={onShare} disabled={!canShare || shareSharing}>
              {shareButtonLabel(shareFeedback, shareSharing)}
            </button>
            <button className="lng-secondary-btn" onClick={openGroupCreate}>
              Play with friends
            </button>
            <button className="lng-secondary-btn" onClick={() => setShowStats(true)}>
              View stats
            </button>
          </div>
          {done && !user && (
            <div className="lng-save-streak">
              <p>Save your streak, past clips, and LangShazam recordings across devices.</p>
              <AuthPanel nextPath={null} onSuccess={() => {}} />
            </div>
          )}
          {sharePreview}
          <PolyvoxPromo placement="langle" language={done.answer?.name} />
          <PolyvoxPopup placement="langle" language={done.answer?.name} trigger={finishedAt} />
          {done && user && (
            <p className="lng-afterthought">
              <Link to="/account">See your games, detections, and recordings</Link>
            </p>
          )}
          <p className="lng-afterthought">
            <Link to="/">Heard it in the wild? Identify it live</Link>
          </p>
        </>
      )}

      {showStats && (
        <StatsModal
          stats={stats}
          puzzleId={meta.puzzleId}
          lastGuessRow={done?.won ? guesses.length : null}
          onClose={() => setShowStats(false)}
          onShare={onShare}
          shareFeedback={shareFeedback}
          shareSharing={shareSharing}
          sharePreview={sharePreview}
          canShare={canShare}
          playerName={playerName}
          onPlayerNameChange={updatePlayerName}
          user={user}
          onSignOut={signOut}
        />
      )}
      {showGroupCreate && (
        <GroupCreateModal
          onClose={closeGroupCreate}
          name={playerName}
          onNameChange={updatePlayerName}
          onCreate={onCreateGroup}
          creating={creatingGroup}
          error={groupError}
          accountName={user?.name}
        />
      )}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}

export default Daily;
